// spotlightAiService.js
const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_API_KEY = process.env.VUE_APP_GROQ_API_KEY || '';

/**
 * Generates an AI deep dive insight based on the Spotlight deterministic algorithms
 * @param {Object} contextData - Context gathered by the AI Spotlight Engine
 * @returns {Promise<string>} - The generated insight in markdown format
 */
const generateSpotlightInsight = async (contextData) => {
  try {
    const {
      triangulation, cluster, trend, dashboardContext,
    } = contextData;

    // Construct the prompt based on available data
    let dataContext = '';

    /* eslint-disable camelcase */
    const indicatorName = dashboardContext?.indicator?.short_name || dashboardContext?.indicator?.full_name || 'the selected health indicator';
    const indicatorDescription = dashboardContext?.indicator?.description || dashboardContext?.indicator?.definition || '';
    const programArea = dashboardContext?.indicator?.program_area || 'Public Health';
    /* eslint-enable camelcase */
    const locationName = dashboardContext?.location?.name || 'the selected region';
    const year = dashboardContext?.year || 'recent years';

    dataContext += `**Indicator:** ${indicatorName}\n`;
    if (indicatorDescription) {
      dataContext += `**Description:** ${indicatorDescription}\n`;
    }
    dataContext += `**Program Area:** ${programArea}\n`;
    dataContext += `**Location:** ${locationName}\n`;
    dataContext += `**Timeframe:** ${year}\n\n`;
    dataContext += '**Statistical Findings from Dashboard:**\n';

    if (triangulation && triangulation.kind === 'anomaly') {
      dataContext += `- Anomaly Detected: There is a ${triangulation.diffPercent}% difference between the highest source (${triangulation.maxSource.name}: ${triangulation.maxSource.value}) and lowest source (${triangulation.minSource.name}: ${triangulation.minSource.value}).\n`;
    } else if (triangulation && triangulation.kind === 'consistent') {
      dataContext += `- Consistency: Data sources are relatively consistent, differing by only ${triangulation.diffPercent}%.\n`;
    }

    if (cluster && cluster.clusters && cluster.clusters.length >= 2) {
      dataContext += `- Clustering: The data splits into a higher coverage cluster (avg ${cluster.clusters[0].average}) and a lower coverage cluster (avg ${cluster.clusters[1].average}).\n`;
    }

    if (trend) {
      dataContext += `- Trend Forecast: The simple linear trend over the last ${trend.yearsUsed} years projects a ${trend.direction} movement from ${trend.currentValue} to ${trend.projectedValue} next year.\n`;
    }

    if (!dataContext) {
      dataContext = 'No significant anomalies, clusters, or trends were detected in the current view.';
    }

    const prompt = `
You are a senior public health data analyst. Review the following automated statistical findings from a health dashboard and provide a short, professional, and insightful "Deep Dive" explanation. 

Data Findings:
${dataContext}

Your Task:
Write a comprehensive, professional "Deep Dive" analysis (up to 3 paragraphs) based *only* on the provided Indicator and Findings. 

Please structure your response as follows:
1. **Context & Significance**: Briefly define what ${indicatorName} measures and why it is critical for ${programArea} in ${locationName}.
2. **Data Interpretation**: Synthesize the "Statistical Findings" above into a clear narrative. Explain the magnitude of any anomalies, clusters, or trends.
3. **Real-World Implications**: Explain *why* these specific data patterns might be happening. What on-the-ground factors (e.g., supply chain, survey methodologies, funding, seasonality) typically cause such discrepancies or trends for this specific indicator?

Rules:
- Use standard markdown for bolding/italics. 
- Do not use markdown headers (e.g., no # or ##). 
- Ensure all sentences are complete and authoritative.
`;

    const response = await fetch(GROQ_API_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${GROQ_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'llama-3.3-70b-versatile',
        messages: [
          {
            role: 'system',
            content: 'You are an expert public health data analyst providing concise, actionable insights.',
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        temperature: 0.4,
        top_p: 0.9,
        max_tokens: 500,
        stream: false,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Groq API error:', errorData);
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message.content.trim();
  } catch (error) {
    console.error('Error generating AI spotlight insight:', error);
    return '**AI Deep Dive Unavailable**\n\nThe LLM service is temporarily unreachable or encountered an error.';
  }
};

export default {
  generateSpotlightInsight,
};
